import { HttpApiEndpoint, HttpApiError, HttpApiGroup, HttpApiSchema } from "effect/http-api";
import { JsonUnknown, JsonUnknownRecord } from "../json-unknown.js";
import { Option, Schema, Effect } from "effect";
import { describeIntRange } from "../openapi-query.js";
import { clamp } from "../clamp.js";
import { UUID } from "../uuid.js";

import {
  RealtimeMetricDefinition,
  RealtimeScoringDefinition,
} from "@bloodline-packages/data-transferring-definitions";

const StandardErrorFields = {
  code: Schema.String,
  message: Schema.String,
  retryable: Schema.Boolean,
  requestId: Schema.String,
  details: Schema.OptionFromOptional(JsonUnknownRecord),
};

const SourceHealthStatusSchema = Schema.Literals([
  "unknown",
  "healthy",
  "stale",
  "degraded",
  "failing",
  "paused",
]);

const FinancialMatchEvidence = Schema.Struct({
  fiscalYear: Schema.Number,
  isConsolidated: Schema.NullOr(Schema.Boolean),
  consolidationScope: Schema.Literals(["consolidated", "unconsolidated", "unknown"]),
  source: Schema.Literals(["valu8", "annual_report"]),
  currencyCode: Schema.OptionFromOptional(Schema.String),
  netSales: Schema.OptionFromOptional(Schema.Number),
  ebitda: Schema.OptionFromOptional(Schema.Number),
});

const RevenueGrowthMatchEvidence = Schema.Struct({
  periodYears: Schema.Literals([1, 3, 5]),
  fromFiscalYear: Schema.Number,
  toFiscalYear: Schema.Number,
  fromNetSales: Schema.Number,
  toNetSales: Schema.Number,
  growthPercent: Schema.Number,
  isConsolidated: Schema.NullOr(Schema.Boolean),
  consolidationScope: Schema.Literals(["consolidated", "unconsolidated", "unknown"]),
  source: Schema.Literals(["valu8", "annual_report_with_deltas"]),
  currencyCode: Schema.String,
});

const EmployeeMatchEvidence = Schema.Struct({
  year: Schema.Number,
  isConsolidated: Schema.NullOr(Schema.Boolean),
  consolidationScope: Schema.Literals(["consolidated", "unconsolidated", "unknown"]),
  source: Schema.Literals(["valu8", "annual_report"]),
  employeeCount: Schema.OptionFromOptional(Schema.Number),
});

const IndustryMatchEvidence = Schema.Struct({
  source: Schema.Literals([
    "sni_classification",
    "industry_code",
    "weighted_industry_code",
    "keyword",
  ]),
  code: Schema.OptionFromOptional(Schema.String),
  label: Schema.String,
  type: Schema.OptionFromOptional(Schema.String),
  matchedTerm: Schema.String,
  confidence: Schema.Literals(["high", "medium", "low"]),
});

const HiringMatchEvidence = Schema.Struct({
  activeJobCount: Schema.Int,
  totalMatchingAds: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  returnedCount: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  hasMore: Schema.Boolean,
  ads: Schema.Array(
    Schema.Struct({
      adId: Schema.String,
      title: Schema.String,
      publishedAt: Schema.String,
      validThrough: Schema.String,
      sourceUrl: Schema.String,
    })
  ),
  source: Schema.Literal("jobtech_active_ads"),
});

const CareerHiringAbsenceReason = Schema.Literals([
  "missing_baseline",
  "baseline_zero",
  "stale_snapshot",
  "future_snapshot",
  "incomplete_snapshot",
  "mapping_changed",
  "scope_changed",
  "ambiguous_domain",
  "ambiguous_identity",
  "latest_attempt_incomplete",
  "no_confirmed_pages",
  "discovery_incomplete",
  "page_incomplete",
  "page_failed",
  "page_deferred",
  "page_cancelled",
  "legacy_worker_missing_evidence",
  "no_observation",
]);

const CareerHiringDeltaMetrics = Schema.Struct({
  count: Schema.OptionFromOptional(Schema.Int),
  ratio: Schema.OptionFromOptional(Schema.Finite),
  baselineAsOf: Schema.OptionFromOptional(Schema.String),
  reason: Schema.OptionFromOptional(CareerHiringAbsenceReason),
});

const CareerHiringScope = Schema.Literals(["company", "group"]);
const CareerHiringFilterScope = Schema.Literals(["company", "group", "any"]);

const CareerHiringMetrics = Schema.Struct({
  scope: CareerHiringScope,
  observedCount: Schema.OptionFromOptional(
    Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0)))
  ),
  asOf: Schema.OptionFromOptional(Schema.String),
  quality: Schema.Literals(["exact", "lower_bound", "unknown"]),
  reason: Schema.OptionFromOptional(CareerHiringAbsenceReason),
  deltas: Schema.Struct({
    previous: CareerHiringDeltaMetrics,
    "1m": CareerHiringDeltaMetrics,
    "3m": CareerHiringDeltaMetrics,
    "6m": CareerHiringDeltaMetrics,
    "1y": CareerHiringDeltaMetrics,
  }),
});

const CareerHiringMatchEvidence = Schema.Struct({
  scope: CareerHiringScope,
  metric: Schema.Literals(["count", "delta_count", "delta_percent"]),
  period: Schema.OptionFromOptional(Schema.Literals(["previous", "1m", "3m", "6m", "1y"])),
  operator: Schema.Literals(["eq", "gt", "gte", "lt", "lte"]),
  expected: Schema.Finite,
  actual: Schema.Finite,
});

const CareerKeywordMatchEvidence = Schema.Struct({
  keyword: Schema.String,
  adCount: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  density: Schema.Finite.pipe(Schema.check(Schema.isBetween({ minimum: 0, maximum: 1 }))),
  analyzedAds: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  scope: CareerHiringScope,
  asOf: Schema.String,
});

const CareerAdMatchEvidenceItem = Schema.Struct({
  adId: Schema.String,
  source: Schema.Literal("career"),
  title: Schema.NullOr(Schema.String),
  sourceUrl: Schema.OptionFromOptional(Schema.String),
  applyUrl: Schema.OptionFromOptional(Schema.String),
  matchedKeywords: Schema.Array(Schema.String),
  domain: Schema.String,
  scope: Schema.Literals(["company", "group"]),
  sourceOrgnr: Schema.String,
  lastSeenAt: Schema.String,
});

const CareerAdsMatchEvidence = Schema.Struct({
  items: Schema.Array(CareerAdMatchEvidenceItem).pipe(Schema.check(Schema.isMaxLength(20))),
  matchingAdCount: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  totalMatchingAds: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  returnedCount: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  hasMore: Schema.Boolean,
  analyzedAds: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  density: Schema.OptionFromOptional(
    Schema.Finite.pipe(Schema.check(Schema.isBetween({ minimum: 0, maximum: 1 })))
  ),
  coverageWarnings: Schema.Array(Schema.String),
});

const CompanyMatchEvidence = Schema.Struct({
  financial: Schema.OptionFromOptional(FinancialMatchEvidence),
  revenueGrowth: Schema.OptionFromOptional(RevenueGrowthMatchEvidence),
  employees: Schema.OptionFromOptional(EmployeeMatchEvidence),
  industry: Schema.OptionFromOptional(IndustryMatchEvidence),
  hiring: Schema.OptionFromOptional(HiringMatchEvidence),
  careerHiring: Schema.OptionFromOptional(CareerHiringMatchEvidence),
  careerKeyword: Schema.OptionFromOptional(CareerKeywordMatchEvidence),
  careerAds: Schema.OptionFromOptional(CareerAdsMatchEvidence),
});

const CompanyGroupMetrics = Schema.Struct({
  identifiedOrgnrCount: Schema.OptionFromOptional(Schema.Number),
  identifiedOrgnrDelta6m: Schema.OptionFromOptional(Schema.Number),
  identifiedOrgnrDelta1y: Schema.OptionFromOptional(Schema.Number),
  identifiedOrgnrDelta3y: Schema.OptionFromOptional(Schema.Number),
  identifiedOrgnrDelta5y: Schema.OptionFromOptional(Schema.Number),
});

const Company = Schema.Struct({
  companyId: Schema.String,
  name: Schema.String,
  domain: Schema.OptionFromOptional(Schema.String),
  industry: Schema.OptionFromOptional(Schema.String),
  city: Schema.OptionFromOptional(Schema.String),
  country: Schema.OptionFromOptional(Schema.String),
  sourceTags: Schema.Array(Schema.String),
  matchEvidence: Schema.OptionFromOptional(CompanyMatchEvidence),
  careerHiringMetrics: Schema.OptionFromOptional(CareerHiringMetrics),
  groupCareerHiringMetrics: Schema.OptionFromOptional(CareerHiringMetrics),
  groupMetrics: Schema.OptionFromOptional(CompanyGroupMetrics),
});

const Filter = Schema.Struct({
  filterId: Schema.String,
  name: Schema.String,
  description: Schema.OptionFromOptional(Schema.String),
  updatedAt: Schema.OptionFromOptional(Schema.String),
});

const FilterResult = Schema.Struct({
  itemId: Schema.String,
  filterId: Schema.String,
  companyId: Schema.String,
  matchedSignals: Schema.Array(Schema.String),
  signalTimestamp: Schema.String,
  sourceTags: Schema.Array(Schema.String),
});

const DeviceAuthStartResponse = Schema.Struct({
  verificationUri: Schema.String,
  verificationUriComplete: Schema.OptionFromOptional(Schema.String),
  userCode: Schema.String,
  deviceCode: Schema.String,
  expiresIn: Schema.Number,
  interval: Schema.Number,
  schemaVersion: Schema.String,
  requestId: Schema.String,
});

const DeviceTokenResponse = Schema.Struct({
  accessToken: Schema.String,
  refreshToken: Schema.OptionFromOptional(Schema.String),
  expiresAt: Schema.String,
  schemaVersion: Schema.String,
  requestId: Schema.String,
});

const RefreshDeviceTokenPayload = Schema.Struct({
  clientId: Schema.String,
  refreshToken: Schema.String,
});

const DeviceTokenPendingResponse = Schema.Struct({
  status: Schema.Literals(["authorization_pending", "slow_down", "expired"]),
  retryAfter: Schema.Number,
  schemaVersion: Schema.String,
  requestId: Schema.String,
});

const PublicActiveClient = Schema.Struct({
  name: Schema.String,
  clientId: Schema.String,
  tenantId: Schema.String,
  zapierClientId: Schema.NullOr(Schema.String),
});

const PublicActiveClientsResponse = Schema.Array(PublicActiveClient);

const StandardError = Schema.Struct(StandardErrorFields);

export const PublicCompanyTimelineInvalidOrgnrError = Schema.Struct({
  ...StandardErrorFields,
  code: Schema.Literal("TIMELINE_INVALID_ORGNR"),
  retryable: Schema.Literal(false),
}).pipe(HttpApiSchema.status(400));

export const PublicCompanyTimelineLoadError = Schema.Struct({
  ...StandardErrorFields,
  code: Schema.Literal("TIMELINE_LOAD_FAILED"),
  retryable: Schema.Literal(true),
}).pipe(HttpApiSchema.status(500));

const FilterListResponse = Schema.Struct({
  items: Schema.Array(Filter),
  schemaVersion: Schema.String,
  requestId: Schema.String,
  asOf: Schema.OptionFromOptional(Schema.String),
});

const FilterRunResponse = Schema.Struct({
  items: Schema.Array(FilterResult),
  cursor: Schema.OptionFromOptional(Schema.String),
  totalMatching: Schema.OptionFromOptional(Schema.Int),
  ignoredFilters: Schema.Array(
    Schema.Struct({
      field: Schema.Literal("since"),
      reason: Schema.String,
    })
  ),
  status: Schema.Literals(["ready", "staged_not_active", "unavailable"]),
  schemaVersion: Schema.String,
  requestId: Schema.String,
  asOf: Schema.OptionFromOptional(Schema.String),
});

export const CompanySearchResponse = Schema.Struct({
  matchMode: Schema.optional(Schema.Literals(["identity", "candidates"])),
  items: Schema.Array(Company),
  cursor: Schema.OptionFromOptional(Schema.String),
  totalEstimate: Schema.OptionFromOptional(Schema.Number),
  schemaVersion: Schema.String,
  requestId: Schema.String,
  asOf: Schema.OptionFromOptional(Schema.String),
  coverage: Schema.Struct({
    financials: Schema.Literals(["not_requested", "matched", "no_match", "unknown"]),
    employees: Schema.Literals(["not_requested", "matched", "no_match", "unknown"]),
    industry: Schema.Literals(["not_requested", "matched", "no_match", "unknown"]),
    group: Schema.optional(Schema.Literals(["not_requested", "matched", "no_match", "unknown"])),
    careerHiring: Schema.OptionFromOptional(
      Schema.Literals(["not_requested", "matched", "no_match", "unknown"])
    ),
    careerAds: Schema.OptionFromOptional(Schema.Literals(["not_requested", "matched", "unknown"])),
    hiring: Schema.OptionFromOptional(
      Schema.Literals(["not_requested", "matched", "no_match", "unknown"])
    ),
  }),
  sourceHealth: Schema.OptionFromOptional(
    Schema.Array(
      Schema.Struct({
        sourceKey: Schema.String,
        status: SourceHealthStatusSchema,
        lastSuccessAt: Schema.OptionFromOptional(Schema.String),
        coverage: Schema.Literals(["complete_snapshot_plus_stream", "unknown"]),
      })
    )
  ),
  /**
   * A place filter that matched no value at all, with the values that do exist
   * beside it. An empty list means every requested place was real, so the
   * emptiness came from somewhere else and is an answer rather than a mistake.
   */
  unmatchedFilters: Schema.Array(
    Schema.Struct({
      field: Schema.Literals(["city", "county"]),
      requested: Schema.Array(Schema.String),
      known: Schema.Array(
        Schema.Struct({
          value: Schema.String,
          companyCount: Schema.Number,
        })
      ),
    })
  ),
  warnings: Schema.Array(
    Schema.Struct({
      code: Schema.String,
      message: Schema.String,
    })
  ),
});

const ContractAwardSearchPayload = Schema.Struct({
  winnerOrgnr: Schema.OptionFromOptional(Schema.NonEmptyString),
  buyer: Schema.OptionFromOptional(Schema.NonEmptyString),
  cpvOrKeywords: Schema.OptionFromOptional(Schema.Array(Schema.NonEmptyString)),
  country: Schema.OptionFromOptional(Schema.NonEmptyString),
  dateFrom: Schema.OptionFromOptional(Schema.NonEmptyString),
  dateTo: Schema.OptionFromOptional(Schema.NonEmptyString),
  minimumValue: Schema.OptionFromOptional(Schema.Finite.check(Schema.isGreaterThanOrEqualTo(0))),
  maximumValue: Schema.OptionFromOptional(Schema.Finite.check(Schema.isGreaterThanOrEqualTo(0))),
  currency: Schema.OptionFromOptional(Schema.NonEmptyString),
  limit: Schema.OptionFromOptional(
    Schema.Int.pipe(Schema.check(Schema.isBetween({ minimum: 1, maximum: 100 })))
  ),
  cursor: Schema.OptionFromOptional(Schema.String),
}).pipe(
  Schema.check(
    Schema.makeFilter(
      (input) =>
        Option.isNone(input.minimumValue) ||
        Option.isNone(input.maximumValue) ||
        input.minimumValue.value <= input.maximumValue.value,
      { message: "minimumValue cannot exceed maximumValue" }
    )
  )
);

const ContractAwardSearchResponse = Schema.Struct({
  items: Schema.Array(
    Schema.Struct({
      publicationNumber: Schema.String,
      noticeVersion: Schema.Int,
      noticeId: UUID,
      awardKey: Schema.String,
      winnerName: Schema.String,
      winnerOrgnr: Schema.OptionFromOptional(Schema.String),
      buyerName: Schema.OptionFromOptional(Schema.String),
      cpvCode: Schema.OptionFromOptional(Schema.String),
      title: Schema.OptionFromOptional(Schema.String),
      decisionDate: Schema.OptionFromOptional(Schema.String),
      publicationDate: Schema.String,
      value: Schema.OptionFromOptional(Schema.Number),
      currency: Schema.OptionFromOptional(Schema.String),
      country: Schema.OptionFromOptional(Schema.String),
      sourceUrl: Schema.OptionFromOptional(Schema.String),
      source: Schema.Literal("ted"),
      winnerMatchMethod: Schema.Literals(["organization_number", "unlinked"]),
    })
  ),
  page: Schema.Struct({
    returned: Schema.Int,
    totalMatching: Schema.OptionFromOptional(Schema.Int),
    hasMore: Schema.Boolean,
    nextCursor: Schema.OptionFromOptional(Schema.String),
  }),
  population: Schema.OptionFromOptional(
    Schema.Struct({
      narrowedBy: Schema.Array(Schema.Literals(["period", "value", "currency"])),
      totalIgnoringNarrowing: Schema.Int,
    })
  ),
  coverage: Schema.Struct({
    status: Schema.Literals(["available", "unknown"]),
    marketCoverage: Schema.Literals(["ted_published_notices", "unknown"]),
    sourceStatus: SourceHealthStatusSchema,
    lastSuccessAt: Schema.OptionFromOptional(Schema.String),
    message: Schema.String,
  }),
  schemaVersion: Schema.String,
  requestId: Schema.String,
});

const GetCompanyResponse = Schema.Struct({
  item: Company,
  schemaVersion: Schema.String,
  requestId: Schema.String,
  asOf: Schema.OptionFromOptional(Schema.String),
});

const GetCompanyDetailsResponse = Schema.Struct({
  companyId: Schema.String,
  companyDetails: JsonUnknownRecord,
  allowlistVersion: Schema.OptionFromOptional(Schema.String),
  schemaVersion: Schema.String,
  requestId: Schema.String,
  asOf: Schema.OptionFromOptional(Schema.String),
});

const IndustryClassificationRelative = Schema.Struct({
  code: Schema.String,
  displayCode: Schema.String,
  label: Schema.String,
  companyCount: Schema.Number,
});

/**
 * `code` is the digit string company search accepts as an industry filter, and
 * `companyCount` is how many classified companies sit under it. A code that
 * exists in the classification but carries no companies is a real answer, and
 * the two fields have to be read together to see it.
 */
const IndustryClassificationCandidate = Schema.Struct({
  code: Schema.String,
  displayCode: Schema.OptionFromOptional(Schema.String),
  label: Schema.String,
  labelSource: Schema.Literals(["sni_classification", "company_classification"]),
  classificationVersion: Schema.OptionFromOptional(Schema.String),
  level: Schema.OptionFromOptional(Schema.Number),
  parent: Schema.OptionFromOptional(IndustryClassificationRelative),
  companyCount: Schema.Number,
  children: Schema.Array(IndustryClassificationRelative),
});

const ResolveIndustryResponse = Schema.Struct({
  candidates: Schema.Array(IndustryClassificationCandidate),
  unresolvedCodes: Schema.Array(Schema.String),
  classifiedCompanyCount: Schema.Number,
  schemaVersion: Schema.String,
  requestId: Schema.String,
  asOf: Schema.OptionFromOptional(Schema.String),
});

export const ResolveIndustryPayload = Schema.Struct({
  codes: Schema.OptionFromOptional(Schema.Array(Schema.String)),
  term: Schema.OptionFromOptional(Schema.String),
  limit: Schema.OptionFromOptional(Schema.Finite),
});

/**
 * A name a source answers to somewhere in the API surface. The same upstream is
 * called different things by different fields — a source tag names a table, an
 * evidence source names a provider, a health key names an ingestion job — so
 * the alias list is what connects a value seen in a result to its source.
 */
const DataSourceAliasSchema = Schema.Struct({
  surface: Schema.Literals([
    "source_health",
    "source_tags",
    "match_evidence",
    "timeline_source",
    "asset_source",
    "metric_source_system",
    "notice_source",
    "coverage_scope",
  ]),
  value: Schema.String,
});

const DataSourceHealthSchema = Schema.Struct({
  sourceKey: Schema.String,
  status: SourceHealthStatusSchema,
  lastSuccessAt: Schema.OptionFromOptional(Schema.String),
  lastErrorMessage: Schema.OptionFromOptional(Schema.String),
});

/**
 * `monitored: false` is an answer rather than a missing one. It means no job
 * reports on this ingestion, so an outage would be silent and an empty result
 * from this source cannot be read as an empty result in the world.
 */
const DataSourceDescription = Schema.Struct({
  key: Schema.String,
  label: Schema.String,
  provider: Schema.String,
  markets: Schema.Array(Schema.String),
  covers: Schema.String,
  tables: Schema.Array(Schema.String),
  limits: Schema.Array(Schema.String),
  aliases: Schema.Array(DataSourceAliasSchema),
  monitored: Schema.Boolean,
  unmonitoredReason: Schema.OptionFromOptional(Schema.String),
  status: Schema.OptionFromOptional(SourceHealthStatusSchema),
  health: Schema.Array(DataSourceHealthSchema),
});

export const DescribeDataSourcesResponse = Schema.Struct({
  items: Schema.Array(DataSourceDescription),
  unknownNames: Schema.Array(Schema.String),
  schemaVersion: Schema.String,
  requestId: Schema.String,
  asOf: Schema.OptionFromOptional(Schema.String),
});

export const DescribeDataSourcesPayload = Schema.Struct({
  names: Schema.OptionFromOptional(Schema.Array(Schema.String)),
});

const TagMatch = Schema.Literals(["contains", "starts_with", "equals"]);

const TaggedCompany = Schema.Struct({
  companyId: Schema.String,
  name: Schema.String,
});

const ClientTagMatch = Schema.Struct({
  tag: Schema.String,
  companyCount: Schema.Number,
  companies: Schema.Array(TaggedCompany),
  companiesTruncated: Schema.Boolean,
});

const SearchClientTagsPage = Schema.Struct({
  returnedTags: Schema.Number,
  totalMatchingTags: Schema.Number,
  truncated: Schema.Boolean,
  returnedCompanies: Schema.Number,
  totalMatchingCompanies: Schema.Number,
  companiesTruncated: Schema.Boolean,
});

const SearchClientTagsDiagnostics = Schema.Struct({
  status: Schema.Literals(["matches", "no_matches", "empty_library"]),
  message: Schema.String,
});

export const SearchClientTagsResponse = Schema.Struct({
  tags: Schema.Array(ClientTagMatch),
  companies: Schema.Array(
    Schema.Struct({
      companyId: Schema.String,
      name: Schema.String,
      tags: Schema.Array(Schema.String),
    })
  ),
  page: SearchClientTagsPage,
  applied: Schema.Struct({
    query: Schema.OptionFromOptional(Schema.String),
    match: TagMatch,
  }),
  sampleTags: Schema.Array(Schema.String),
  diagnostics: SearchClientTagsDiagnostics,
  schemaVersion: Schema.String,
  requestId: Schema.String,
  asOf: Schema.OptionFromOptional(Schema.String),
});

export const SearchClientTagsPayload = Schema.Struct({
  query: Schema.OptionFromOptional(Schema.String),
  match: Schema.OptionFromOptional(TagMatch),
  limit: Schema.OptionFromOptional(Schema.Finite),
  companyLimit: Schema.OptionFromOptional(Schema.Finite),
});

export const CompanySearchFilters = Schema.Struct({
  where: Schema.OptionFromOptional(Schema.String),
  minRevenue: Schema.OptionFromOptional(Schema.Finite),
  maxRevenue: Schema.OptionFromOptional(Schema.Finite),
  financialCurrency: Schema.OptionFromOptional(Schema.String),
  financialsConsolidated: Schema.OptionFromOptional(Schema.Boolean),
  minEmployees: Schema.OptionFromOptional(Schema.Finite),
  maxEmployees: Schema.OptionFromOptional(Schema.Finite),
  employeesConsolidated: Schema.OptionFromOptional(Schema.Boolean),
  minEbitda: Schema.OptionFromOptional(Schema.Finite),
  maxEbitda: Schema.OptionFromOptional(Schema.Finite),
  positiveRevenueGrowth: Schema.OptionFromOptional(Schema.Boolean),
  minRevenueGrowthPercent: Schema.OptionFromOptional(Schema.Finite),
  maxRevenueGrowthPercent: Schema.OptionFromOptional(Schema.Finite),
  revenueGrowthPeriodYears: Schema.OptionFromOptional(Schema.Literals([1, 3, 5])),
  activeJobTitles: Schema.OptionFromOptional(Schema.Array(Schema.String)),
  minActiveJobCount: Schema.OptionFromOptional(
    Schema.Int.pipe(Schema.check(Schema.isBetween({ minimum: 1, maximum: 1_000 })))
  ),
  city: Schema.OptionFromOptional(Schema.String),
  county: Schema.OptionFromOptional(Schema.String),
  counties: Schema.OptionFromOptional(Schema.Array(Schema.String)),
  country: Schema.OptionFromOptional(Schema.String),
  industry: Schema.OptionFromOptional(Schema.String),
  minGroupCompanyCount: Schema.OptionFromOptional(Schema.Finite),
  maxGroupCompanyCount: Schema.OptionFromOptional(Schema.Finite),
  minGroupCompanyCountDelta6m: Schema.OptionFromOptional(Schema.Finite),
  minGroupCompanyCountDelta1y: Schema.OptionFromOptional(Schema.Finite),
  minGroupCompanyCountDelta3y: Schema.OptionFromOptional(Schema.Finite),
  minGroupCompanyCountDelta5y: Schema.OptionFromOptional(Schema.Finite),
  careerHiring: Schema.OptionFromOptional(
    Schema.Struct({
      scope: Schema.OptionFromOptional(CareerHiringFilterScope),
      metric: Schema.Literals(["count", "delta_count", "delta_percent"]),
      period: Schema.OptionFromOptional(Schema.Literals(["previous", "1m", "3m", "6m", "1y"])),
      operator: Schema.Literals(["eq", "gt", "gte", "lt", "lte"]),
      value: Schema.Finite,
    })
  ),
  careerKeyword: Schema.OptionFromOptional(
    Schema.Struct({
      keyword: Schema.NonEmptyString,
      scope: Schema.OptionFromOptional(CareerHiringFilterScope),
      minAdCount: Schema.OptionFromOptional(
        Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0)))
      ),
      minDensity: Schema.OptionFromOptional(
        Schema.Finite.pipe(Schema.check(Schema.isBetween({ minimum: 0, maximum: 1 })))
      ),
    })
  ),
  careerAds: Schema.OptionFromOptional(
    Schema.Struct({
      roles: Schema.OptionFromOptional(
        Schema.Array(Schema.NonEmptyString.pipe(Schema.check(Schema.isPattern(/\S/u)))).pipe(
          Schema.check(Schema.isMinLength(1)),
          Schema.check(Schema.isMaxLength(20))
        )
      ),
      keywords: Schema.OptionFromOptional(
        Schema.Array(Schema.NonEmptyString.pipe(Schema.check(Schema.isPattern(/\S/u)))).pipe(
          Schema.check(Schema.isMinLength(1)),
          Schema.check(Schema.isMaxLength(20))
        )
      ),
      scope: Schema.OptionFromOptional(CareerHiringFilterScope),
      minAdCount: Schema.OptionFromOptional(Schema.Int.pipe(Schema.check(Schema.isGreaterThan(0)))),
      minDensity: Schema.OptionFromOptional(
        Schema.Finite.pipe(Schema.check(Schema.isBetween({ minimum: 0, maximum: 1 })))
      ),
    })
  ),
  includeCareerHiringMetrics: Schema.OptionFromOptional(Schema.Boolean),
}).pipe(
  Schema.check(
    Schema.makeFilter(
      (filters) =>
        Option.isNone(filters.minRevenue) ||
        Option.isNone(filters.maxRevenue) ||
        filters.minRevenue.value <= filters.maxRevenue.value,
      { message: "minRevenue cannot exceed maxRevenue" }
    )
  ),
  Schema.check(
    Schema.makeFilter(
      (filters) =>
        Option.isNone(filters.minEmployees) ||
        Option.isNone(filters.maxEmployees) ||
        filters.minEmployees.value <= filters.maxEmployees.value,
      { message: "minEmployees cannot exceed maxEmployees" }
    )
  ),
  Schema.check(
    Schema.makeFilter(
      (filters) =>
        Option.isNone(filters.minEbitda) ||
        Option.isNone(filters.maxEbitda) ||
        filters.minEbitda.value <= filters.maxEbitda.value,
      { message: "minEbitda cannot exceed maxEbitda" }
    )
  ),
  Schema.check(
    Schema.makeFilter(
      (filters) =>
        Option.isNone(filters.minRevenueGrowthPercent) ||
        Option.isNone(filters.maxRevenueGrowthPercent) ||
        filters.minRevenueGrowthPercent.value <= filters.maxRevenueGrowthPercent.value,
      { message: "minRevenueGrowthPercent cannot exceed maxRevenueGrowthPercent" }
    )
  ),
  Schema.check(
    Schema.makeFilter(
      (filters) =>
        Option.isNone(filters.minGroupCompanyCount) ||
        Option.isNone(filters.maxGroupCompanyCount) ||
        filters.minGroupCompanyCount.value <= filters.maxGroupCompanyCount.value,
      { message: "minGroupCompanyCount cannot exceed maxGroupCompanyCount" }
    )
  ),
  Schema.check(
    Schema.makeFilter(
      (filters) =>
        Option.isNone(filters.careerHiring) ||
        (filters.careerHiring.value.metric === "count"
          ? Option.isNone(filters.careerHiring.value.period) &&
            Number.isInteger(filters.careerHiring.value.value) &&
            filters.careerHiring.value.value >= 0
          : filters.careerHiring.value.metric === "delta_count"
            ? Option.isSome(filters.careerHiring.value.period) &&
              Number.isInteger(filters.careerHiring.value.value)
            : Option.isSome(filters.careerHiring.value.period)),
      {
        message:
          "careerHiring count requires a non-negative integer and forbids period; delta_count requires an integer and delta metrics require period",
      }
    )
  ),
  Schema.check(
    Schema.makeFilter(
      (filters) =>
        Option.isNone(filters.careerAds) ||
        ((Option.isSome(filters.careerAds.value.roles) ||
          Option.isSome(filters.careerAds.value.keywords)) &&
          (Option.isNone(filters.careerAds.value.minDensity) ||
            Option.isSome(filters.careerAds.value.keywords))),
      {
        message: "careerAds requires roles or keywords; minDensity requires at least one keyword",
      }
    )
  )
);

export const TimelineAction = Schema.Struct({
  type: Schema.Literals([
    "get_company_details",
    "get_company_structure",
    "get_annual_reports",
    "get_timeline_page",
    "open_source_url",
  ]),
  label: Schema.String,
  method: Schema.Literals(["GET", "POST"]),
  href: Schema.String,
  body: Schema.OptionFromOptional(JsonUnknownRecord),
});

export const TimelineCompanyReference = Schema.Struct({
  orgnr: Schema.OptionFromOptional(Schema.String),
  name: Schema.OptionFromOptional(Schema.String),
  valu8Id: Schema.OptionFromOptional(Schema.Number),
});

export const TimelineStructureSummary = Schema.Struct({
  available: Schema.Boolean,
  rootValu8Id: Schema.OptionFromOptional(Schema.Number),
  ultimateParent: Schema.OptionFromOptional(TimelineCompanyReference),
  immediateParent: Schema.OptionFromOptional(TimelineCompanyReference),
  directChildrenCount: Schema.Number,
  totalCompanyCount: Schema.Number,
  totalRelationCount: Schema.Number,
});

export const TimelineCompanyOverview = Schema.Struct({
  orgnr: Schema.String,
  name: Schema.String,
  valu8Id: Schema.OptionFromOptional(Schema.Number),
  url: Schema.OptionFromOptional(Schema.String),
  city: Schema.OptionFromOptional(Schema.String),
  countryCode: Schema.OptionFromOptional(Schema.String),
  operationalStatus: Schema.OptionFromOptional(Schema.String),
  registrationDate: Schema.OptionFromOptional(Schema.String),
  ultimateParent: Schema.OptionFromOptional(TimelineCompanyReference),
  hasGroupStructure: Schema.Boolean,
  structureSummary: TimelineStructureSummary,
});

export const TimelineEvent = Schema.Struct({
  id: Schema.String,
  eventType: Schema.Literals([
    "news_article",
    "annual_report_insight",
    "annual_report_filing",
    "company_move",
    "linkedin_snapshot",
    "job_posting",
    "public_contract_award",
    "workplace_opened",
    "workplace_closed",
    "workplace_moved",
    "workplace_size_changed",
    "group_member_added",
    "group_member_removed",
    "group_parent_changed",
    "group_ownership_changed",
    "group_ultimate_parent_changed",
    "group_country_entered",
  ]),
  date: Schema.String,
  title: Schema.String,
  summary: Schema.OptionFromOptional(Schema.String),
  details: Schema.OptionFromOptional(JsonUnknownRecord),
  source: Schema.String,
  sourceType: Schema.String,
  sourceLabel: Schema.String,
  sourceUrl: Schema.OptionFromOptional(Schema.String),
  dateSource: Schema.String,
  datePrecision: Schema.Literals(["date", "datetime"]),
  dateEstimated: Schema.Boolean,
  actions: Schema.Array(TimelineAction),
});

export const CompanyTimelineSourceError = Schema.Struct({
  sourceType: Schema.String,
  sourceLabel: Schema.String,
  message: Schema.String,
});

export const CompanyTimelineExcludedEvents = Schema.Struct({
  sourceType: Schema.String,
  sourceLabel: Schema.String,
  count: Schema.Number,
  reason: Schema.String,
});

export const CompanyTimelineResponse = Schema.Struct({
  orgnr: Schema.String,
  companyName: Schema.OptionFromOptional(Schema.String),
  company: TimelineCompanyOverview,
  actions: Schema.Array(TimelineAction),
  events: Schema.Array(TimelineEvent),
  totalCount: Schema.Number,
  offset: Schema.Number,
  limit: Schema.Number,
  sourceErrors: Schema.Array(CompanyTimelineSourceError),
  excludedEventCount: Schema.Number,
  excludedEvents: Schema.Array(CompanyTimelineExcludedEvents),
  schemaVersion: Schema.String,
  requestId: Schema.String,
});

export const CompanyStructureCompanyReference = Schema.Struct({
  orgnr: Schema.OptionFromOptional(Schema.String),
  name: Schema.OptionFromOptional(Schema.String),
});

export const CompanyStructureSummary = Schema.Struct({
  available: Schema.Boolean,
  ultimateParent: Schema.OptionFromOptional(CompanyStructureCompanyReference),
  immediateParent: Schema.OptionFromOptional(CompanyStructureCompanyReference),
  directChildrenCount: Schema.Number,
  totalCompanyCount: Schema.Number,
  totalRelationCount: Schema.Number,
});

export const CompanyStructureOverview = Schema.Struct({
  orgnr: Schema.String,
  name: Schema.String,
  url: Schema.OptionFromOptional(Schema.String),
  city: Schema.OptionFromOptional(Schema.String),
  countryCode: Schema.OptionFromOptional(Schema.String),
  operationalStatus: Schema.OptionFromOptional(Schema.String),
  registrationDate: Schema.OptionFromOptional(Schema.String),
  ultimateParent: Schema.OptionFromOptional(CompanyStructureCompanyReference),
  hasGroupStructure: Schema.Boolean,
  structureSummary: CompanyStructureSummary,
});

/** Stable legacy response for clients using the original structure route. */
export const CompanyStructureCompany = Schema.Struct({
  valu8Id: Schema.Number,
  name: Schema.String,
  orgnr: Schema.OptionFromOptional(Schema.String),
  companyType: Schema.OptionFromOptional(Schema.String),
  countryCode: Schema.OptionFromOptional(Schema.String),
  hasIdentifier: Schema.Boolean,
  ultimateParentValu8Id: Schema.OptionFromOptional(Schema.Number),
});

export const CompanyStructureRelation = Schema.Struct({
  parentValu8Id: Schema.Number,
  childValu8Id: Schema.Number,
  ultimateParentValu8Id: Schema.OptionFromOptional(Schema.Number),
  /** Percentage points from 0 to 100; 100 represents 100% ownership. */
  ownershipStake: Schema.OptionFromOptional(Schema.Number),
  /** Percentage points from 0 to 100; 100 represents 100% ownership. */
  minOwnershipStake: Schema.OptionFromOptional(Schema.Number),
  /** Percentage points from 0 to 100; 100 represents 100% ownership. */
  maxOwnershipStake: Schema.OptionFromOptional(Schema.Number),
});

export const CompanyStructureResponse = Schema.Struct({
  company: TimelineCompanyOverview,
  rootValu8Id: Schema.OptionFromOptional(Schema.Number),
  companies: Schema.Array(CompanyStructureCompany),
  relations: Schema.Array(CompanyStructureRelation),
  totalCompanyCount: Schema.Number,
  totalRelationCount: Schema.Number,
  schemaVersion: Schema.String,
  requestId: Schema.String,
});

export const CompanyStructureAsOfCompany = Schema.Struct({
  memberId: Schema.String,
  name: Schema.String,
  orgnr: Schema.OptionFromOptional(Schema.String),
  companyType: Schema.OptionFromOptional(Schema.String),
  countryCode: Schema.OptionFromOptional(Schema.String),
  hasIdentifier: Schema.Boolean,
  ultimateParentMemberId: Schema.OptionFromOptional(Schema.String),
});

export const CompanyStructureAsOfRelation = Schema.Struct({
  parentMemberId: Schema.String,
  childMemberId: Schema.String,
  ultimateParentMemberId: Schema.OptionFromOptional(Schema.String),
  /** Fraction from 0 to 1; 1 represents 100% ownership. */
  ownershipStake: Schema.OptionFromOptional(Schema.Number),
  /** Fractions from 0 to 1; 1 represents 100% ownership. */
  minOwnershipStake: Schema.OptionFromOptional(Schema.Number),
  /** Fraction from 0 to 1; 1 represents 100% ownership. */
  maxOwnershipStake: Schema.OptionFromOptional(Schema.Number),
});

export const CompanyStructureAsOfResponse = Schema.Struct({
  company: CompanyStructureOverview,
  asOf: Schema.String,
  observationCoverage: Schema.Struct({
    coverageStart: Schema.OptionFromOptional(Schema.String),
    hasSnapshot: Schema.Boolean,
  }),
  rootMemberId: Schema.OptionFromOptional(Schema.String),
  companies: Schema.Array(CompanyStructureAsOfCompany),
  relations: Schema.Array(CompanyStructureAsOfRelation),
  totalCompanyCount: Schema.Number,
  totalRelationCount: Schema.Number,
  schemaVersion: Schema.String,
  requestId: Schema.String,
});

const CompanyTimelineUrlParams = Schema.Struct({
  offset: Schema.NumberFromString.pipe(
    Schema.check(Schema.isInt()),
    Schema.check(Schema.isGreaterThanOrEqualTo(0)),
    describeIntRange({ minimum: 0 })
  ).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => 0)),
    Schema.withConstructorDefault(Effect.sync(() => 0))
  ),
  limit: Schema.NumberFromString.pipe(
    Schema.check(Schema.isInt()),
    clamp(1, 500),
    describeIntRange({ minimum: 1, maximum: 500 })
  ).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => 100)),
    Schema.withConstructorDefault(Effect.sync(() => 100))
  ),
});

const CompanyStructureUrlParams = Schema.Struct({
  asOf: Schema.OptionFromOptional(
    Schema.String.pipe(
      Schema.check(
        Schema.makeFilter(
          (value) => {
            if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
            const date = new Date(`${value}T00:00:00.000Z`);
            return (
              !Number.isNaN(date.getTime()) &&
              date.toISOString().slice(0, 10) === value &&
              value <= new Date().toISOString().slice(0, 10)
            );
          },
          {
            message:
              "asOf must be a valid calendar date in YYYY-MM-DD format and not be in the future",
          }
        )
      )
    )
  ),
});

const filterId = Schema.String;
const heatFilterId = UUID;
const companyId = Schema.String;
const orgnr = Schema.String;

/** Bounded pagination for the raw-source public filter projection. */
export const PublicFilterRunPayload = Schema.Struct({
  since: Schema.OptionFromOptional(Schema.String),
  limit: Schema.OptionFromOptional(
    Schema.Number.pipe(
      Schema.check(Schema.isInt()),
      Schema.check(Schema.isGreaterThan(0)),
      Schema.check(Schema.isLessThanOrEqualTo(100))
    )
  ),
  cursor: Schema.OptionFromOptional(
    Schema.NumberFromString.pipe(
      Schema.check(Schema.isInt()),
      Schema.check(Schema.isGreaterThanOrEqualTo(0))
    )
  ),
});

/** A bounded, non-persistent evaluation of a metric-v1 heat-filter definition. */
export const PublicHeatFilterPreviewPayload = Schema.Struct({
  definition: RealtimeScoringDefinition,
  limit: Schema.Int.pipe(
    Schema.check(Schema.isGreaterThan(0)),
    Schema.check(Schema.isLessThanOrEqualTo(100))
  ).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => 20)),
    Schema.withConstructorDefault(Effect.sync(() => 20))
  ),
});

/** The exact private, staged definition to create for one authenticated client. */
export const PublicHeatFilterCreatePayload = Schema.Struct({
  name: Schema.String.pipe(
    Schema.check(Schema.isMinLength(1)),
    Schema.check(Schema.isMaxLength(120))
  ),
  definition: RealtimeScoringDefinition,
  idempotencyKey: Schema.String.pipe(
    Schema.check(Schema.isMinLength(1)),
    Schema.check(Schema.isMaxLength(128))
  ),
});

/**
 * `expectedRevision` is the revision the caller believes it is replacing. The
 * server refuses the write when it no longer matches, so two authoring clients
 * cannot silently overwrite each other's definition.
 */
export const PublicHeatFilterUpdatePayload = Schema.Struct({
  definition: RealtimeScoringDefinition,
  expectedRevision: Schema.Int.pipe(Schema.check(Schema.isGreaterThan(0))),
});

/**
 * Activation is a separate call from the definition write: a staged definition
 * is meant to be previewed and reviewed before it starts producing live results.
 */
export const PublicHeatFilterActivationPayload = Schema.Struct({
  executionMode: Schema.Literals(["legacy_live", "metric_v1"]),
  expectedRevision: Schema.Int.pipe(Schema.check(Schema.isGreaterThan(0))),
});

const PublicHeatFilterMetricCatalogResponse = Schema.Struct({
  version: Schema.Literal("v1"),
  metrics: Schema.Array(RealtimeMetricDefinition),
});

const PublicHeatFilterPreviewResponse = Schema.Struct({
  definition: RealtimeScoringDefinition,
  metadata: Schema.Struct({
    count: Schema.Number,
    limit: Schema.Number,
  }),
  data: Schema.Array(JsonUnknown),
  persistence: Schema.Literal("not_saved"),
  /** Reasons an empty or unexpectedly small preview may not mean "no matches". */
  warnings: Schema.Array(
    Schema.Struct({
      code: Schema.String,
      message: Schema.String,
    })
  ),
});

/**
 * A newly created filter is live on `metric_v1`. Both modes stay in the schema
 * because a replayed idempotency key can resolve to a filter the caller has
 * since returned to `legacy_live` — the response then describes what the filter
 * actually is rather than refusing to answer.
 */
const PublicHeatFilterCreateResponse = Schema.Struct({
  heatFilterId: UUID,
  name: Schema.String,
  revision: Schema.Number,
  executionMode: Schema.Literals(["legacy_live", "metric_v1"]),
  definition: RealtimeScoringDefinition,
  isPrivate: Schema.Literal(true),
  idempotencyReused: Schema.Boolean,
  status: Schema.Literals(["ready", "staged_not_active"]),
});

/**
 * The state of one heat filter after a write. `revision` is what the next write
 * must present as `expectedRevision`, so it is always echoed back.
 */
const PublicHeatFilterStateResponse = Schema.Struct({
  heatFilterId: UUID,
  name: Schema.String,
  revision: Schema.Number,
  executionMode: Schema.Literals(["legacy_live", "metric_v1", "manual_sql"]),
  // Absent on a filter that has no staged metric-v1 definition at all, which a
  // client-owned filter can be before its first definition write.
  definition: Schema.OptionFromOptional(RealtimeScoringDefinition),
  isPrivate: Schema.Boolean,
  status: Schema.Literals(["staged_not_active", "ready"]),
});

/**
 * Authoring errors carry a body: an authoring client cannot correct a rejected
 * definition from a bare status code, so `details.issues` names the exact paths
 * that failed validation.
 */
export const PublicHeatFilterDefinitionInvalidError = Schema.Struct({
  ...StandardErrorFields,
  code: Schema.Literal("HEAT_FILTER_DEFINITION_INVALID"),
  retryable: Schema.Literal(false),
}).pipe(HttpApiSchema.status(400));

export const PublicHeatFilterSourceUnavailableError = Schema.Struct({
  ...StandardErrorFields,
  code: Schema.Literal("HEAT_FILTER_SOURCE_UNAVAILABLE"),
  retryable: Schema.Literal(true),
}).pipe(HttpApiSchema.status(503));

export const PublicHeatFilterIdempotencyConflictError = Schema.Struct({
  ...StandardErrorFields,
  code: Schema.Literal("HEAT_FILTER_IDEMPOTENCY_CONFLICT"),
  retryable: Schema.Literal(false),
}).pipe(HttpApiSchema.status(409));

/**
 * Retryable, because the caller resolves it by re-reading the filter and
 * replaying the write against the revision the server actually holds.
 */
export const PublicHeatFilterRevisionConflictError = Schema.Struct({
  ...StandardErrorFields,
  code: Schema.Literal("HEAT_FILTER_REVISION_CONFLICT"),
  retryable: Schema.Literal(true),
}).pipe(HttpApiSchema.status(409));

/**
 * Activation refused on a governance or compatibility ground rather than on the
 * definition itself — the message names which gate blocked it.
 */
export const PublicHeatFilterActivationBlockedError = Schema.Struct({
  ...StandardErrorFields,
  code: Schema.Literal("HEAT_FILTER_ACTIVATION_BLOCKED"),
  retryable: Schema.Literal(false),
}).pipe(HttpApiSchema.status(409));

/**
 * `retryable` stays a boolean here: a transient database fault is worth another
 * attempt, while a decode fault is permanent until the server is fixed, and a
 * retrying client needs to tell the two apart.
 */
export const PublicHeatFilterRequestFailedError = Schema.Struct({
  ...StandardErrorFields,
  code: Schema.Literal("HEAT_FILTER_REQUEST_FAILED"),
}).pipe(HttpApiSchema.status(500));

export const PublicHealthGroup = HttpApiGroup.make("publicHealth").add(
  HttpApiEndpoint.get("getHealthz", "/cli/health", {
    success: Schema.Struct({
      status: Schema.String,
      timestamp: Schema.String,
    }),
  })
);

export const PublicAuthGroup = HttpApiGroup.make("publicAuth")
  .add(
    HttpApiEndpoint.post("startDeviceAuthorization", "/cli/auth/device/start", {
      payload: Schema.Struct({
        clientId: Schema.String,
        scopes: Schema.OptionFromOptional(Schema.String),
      }),
      success: DeviceAuthStartResponse,
      error: [HttpApiError.BadRequestNoContent, StandardError],
    })
  )
  .add(
    HttpApiEndpoint.post("pollDeviceToken", "/cli/auth/device/token", {
      payload: Schema.Struct({
        clientId: Schema.String,
        deviceCode: Schema.String,
        clientSecret: Schema.OptionFromOptional(Schema.String),
      }),
      success: Schema.Union([DeviceTokenResponse, DeviceTokenPendingResponse]),
      error: [HttpApiError.BadRequestNoContent, StandardError],
    })
  )
  .add(
    HttpApiEndpoint.post("refreshDeviceToken", "/cli/auth/device/refresh", {
      payload: RefreshDeviceTokenPayload,
      success: DeviceTokenResponse,
      error: [HttpApiError.BadRequestNoContent, StandardError],
    })
  )
  .add(
    HttpApiEndpoint.get("listPublicActiveClients", "/cli/clients/active", {
      success: PublicActiveClientsResponse,
      error: StandardError,
    })
  );

export const PublicFiltersGroup = HttpApiGroup.make("publicFilters")
  .add(
    HttpApiEndpoint.get("listPublicFilters", "/cli/filters", {
      success: FilterListResponse,
      error: StandardError,
    })
  )
  .add(
    HttpApiEndpoint.post("runPublicFilter", "/cli/filters/:filterId/run", {
      params: { filterId },
      payload: PublicFilterRunPayload,
      success: FilterRunResponse,
      error: [HttpApiError.BadRequestNoContent, HttpApiError.ForbiddenNoContent, StandardError],
    })
  );

/** Authenticated authoring surface used by MCP and other public API clients. */
export const PublicHeatFilterAuthoringGroup = HttpApiGroup.make("publicHeatFilterAuthoring")
  .add(
    HttpApiEndpoint.get("getPublicHeatFilterMetricCatalog", "/cli/heat-filters/metric-catalog", {
      success: PublicHeatFilterMetricCatalogResponse,
      error: HttpApiError.InternalServerErrorNoContent,
    })
  )
  .add(
    HttpApiEndpoint.post("previewPublicHeatFilter", "/cli/heat-filters/preview", {
      payload: PublicHeatFilterPreviewPayload,
      success: PublicHeatFilterPreviewResponse,
      error: [
        PublicHeatFilterDefinitionInvalidError,
        PublicHeatFilterSourceUnavailableError,
        PublicHeatFilterRequestFailedError,
      ],
    })
  )
  .add(
    HttpApiEndpoint.post("createPublicHeatFilter", "/cli/heat-filters", {
      payload: PublicHeatFilterCreatePayload,
      success: PublicHeatFilterCreateResponse,
      error: [
        PublicHeatFilterDefinitionInvalidError,
        PublicHeatFilterIdempotencyConflictError,
        // Creation now activates, so it inherits activation's readiness gate.
        PublicHeatFilterSourceUnavailableError,
        PublicHeatFilterRequestFailedError,
      ],
    })
  )
  .add(
    HttpApiEndpoint.get("getPublicHeatFilter", "/cli/heat-filters/:heatFilterId", {
      params: { heatFilterId },
      success: PublicHeatFilterStateResponse,
      error: [
        HttpApiError.BadRequestNoContent,
        HttpApiError.NotFoundNoContent,
        HttpApiError.ForbiddenNoContent,
        PublicHeatFilterRequestFailedError,
      ],
    })
  )
  .add(
    HttpApiEndpoint.put(
      "updatePublicHeatFilterDefinition",
      "/cli/heat-filters/:heatFilterId/definition",
      {
        params: { heatFilterId },
        payload: PublicHeatFilterUpdatePayload,
        success: PublicHeatFilterStateResponse,
        error: [
          HttpApiError.NotFoundNoContent,
          HttpApiError.ForbiddenNoContent,
          PublicHeatFilterDefinitionInvalidError,
          PublicHeatFilterRevisionConflictError,
          PublicHeatFilterRequestFailedError,
        ],
      }
    )
  )
  .add(
    HttpApiEndpoint.put(
      "setPublicHeatFilterExecutionMode",
      "/cli/heat-filters/:heatFilterId/execution-mode",
      {
        params: { heatFilterId },
        payload: PublicHeatFilterActivationPayload,
        success: PublicHeatFilterStateResponse,
        error: [
          HttpApiError.BadRequestNoContent,
          HttpApiError.NotFoundNoContent,
          HttpApiError.ForbiddenNoContent,
          PublicHeatFilterActivationBlockedError,
          PublicHeatFilterRevisionConflictError,
          PublicHeatFilterSourceUnavailableError,
          PublicHeatFilterRequestFailedError,
        ],
      }
    )
  );

export const PublicCompaniesGroup = HttpApiGroup.make("publicCompanies")
  .add(
    HttpApiEndpoint.post("searchPublicCompanies", "/cli/companies/search", {
      payload: Schema.Struct({
        query: Schema.String,
        matchMode: Schema.optional(Schema.Literal("identity")),
        orgnrLookup: Schema.OptionFromOptional(Schema.Boolean),
        filters: Schema.OptionFromOptional(CompanySearchFilters),
        sort: Schema.OptionFromOptional(JsonUnknownRecord),
        limit: Schema.OptionFromOptional(Schema.Finite),
        cursor: Schema.OptionFromOptional(Schema.String),
      }),
      success: CompanySearchResponse,
      error: [HttpApiError.BadRequestNoContent, StandardError],
    })
  )
  .add(
    HttpApiEndpoint.get("getPublicCompany", "/cli/companies/:companyId", {
      params: { companyId },
      success: GetCompanyResponse,
      error: [HttpApiError.BadRequestNoContent, StandardError],
    })
  )
  .add(
    HttpApiEndpoint.get("getCompanyTimeline", "/cli/companies/:orgnr/timeline", {
      params: { orgnr },
      query: CompanyTimelineUrlParams,
      success: CompanyTimelineResponse,
      error: [PublicCompanyTimelineInvalidOrgnrError, PublicCompanyTimelineLoadError],
    })
  )
  .add(
    HttpApiEndpoint.get("getCompanyStructure", "/cli/companies/:orgnr/structure", {
      params: { orgnr },
      success: CompanyStructureResponse,
      error: [PublicCompanyTimelineInvalidOrgnrError, PublicCompanyTimelineLoadError],
    })
  )
  .add(
    HttpApiEndpoint.get("getCompanyStructureAsOf", "/cli/companies/:orgnr/structure/as-of", {
      params: { orgnr },
      query: CompanyStructureUrlParams,
      success: CompanyStructureAsOfResponse,
      error: [PublicCompanyTimelineInvalidOrgnrError, PublicCompanyTimelineLoadError],
    })
  )
  .add(
    HttpApiEndpoint.post("findContractAwards", "/cli/procurement/awards/search", {
      payload: ContractAwardSearchPayload,
      success: ContractAwardSearchResponse,
      error: [HttpApiError.BadRequestNoContent, StandardError],
    })
  )
  .add(
    HttpApiEndpoint.post("resolveIndustry", "/cli/industries/resolve", {
      payload: ResolveIndustryPayload,
      success: ResolveIndustryResponse,
      error: [HttpApiError.BadRequestNoContent, StandardError],
    })
  )
  .add(
    HttpApiEndpoint.post("describeDataSources", "/cli/data-sources/describe", {
      payload: DescribeDataSourcesPayload,
      success: DescribeDataSourcesResponse,
      error: [HttpApiError.BadRequestNoContent, StandardError],
    })
  );

export const PublicClientTagsGroup = HttpApiGroup.make("publicClientTags").add(
  HttpApiEndpoint.post("searchClientTags", "/cli/tags/search", {
    payload: SearchClientTagsPayload,
    success: SearchClientTagsResponse,
    error: [HttpApiError.BadRequestNoContent, StandardError],
  })
);

export const PublicCompanyDetailsGroup = HttpApiGroup.make("publicCompanyDetails").add(
  HttpApiEndpoint.post("getPublicCompanyDetails", "/cli/company-details/:companyId", {
    params: { companyId },
    payload: Schema.Struct({
      columns: Schema.OptionFromOptional(Schema.Array(Schema.String)),
    }),
    success: GetCompanyDetailsResponse,
    error: [HttpApiError.BadRequestNoContent, StandardError],
  })
);
